import { Suspense, useMemo } from 'react'
import { Outlet, useNavigate, useLocation } from 'react-router-dom'
import {
  EuShell,
  EuSidebar,
  EU_METRIX_ICON_RULES,
  Spinner,
  type EuNavGroup,
  type EuNavItem,
  type EuIconName,
} from '@newamsterdam/ui'
import { useUser } from '../store/user'
import { UserMenu } from './UserMenu'
import { CompanyContextBridge } from '../components/CompanyContextBridge'
import styles from './Shell.module.css'

const ICONS = EU_METRIX_ICON_RULES

interface ModuleNavItem extends EuNavItem {
  module?: string
  permissions?: string[]
}

interface ModuleNav {
  module?: string
  label: string
  icon?: EuIconName
  items: ModuleNavItem[]
}

const MODULE_NAVS: ModuleNav[] = [
  {
    module: 'budget',
    label: 'Projekty',
    icon: ICONS.sidebarGroups.projects,
    items: [
      {
        id: 'projects',
        label: 'Lista projektów',
        icon: ICONS.sidebarItems.projectList,
        href: '/projects',
      },
    ],
  },
  {
    module: 'budget',
    label: 'Sprzedaż',
    icon: 'chartLine',
    items: [
      {
        id: 'sales-forecast',
        label: 'Prognoza sprzedaży',
        icon: 'chart',
        href: '/sales/forecast',
        permissions: ['view_sales_forecast'],
      },
    ],
  },
  {
    module: 'cash_position',
    label: 'Raporty',
    icon: ICONS.sidebarGroups.reports,
    items: [
      {
        id: 'cashflow',
        label: 'Cashflow',
        icon: ICONS.sidebarItems.reports,
        href: '/cashflow',
        permissions: ['view_company_finance', 'view_all'],
      },
    ],
  },
  {
    module: 'people',
    label: 'Personel',
    icon: ICONS.sidebarGroups.personnel,
    items: [
      {
        id: 'employees',
        label: 'Pracownicy',
        icon: ICONS.sidebarItems.employee,
        href: '/people/employees',
      },
      { id: 'rates', label: 'Stawki', icon: ICONS.sidebarItems.rates, href: '/people/rates' },
    ],
  },
  {
    module: 'finance',
    label: 'Finanse',
    icon: ICONS.sidebarGroups.finance,
    items: [
      {
        id: 'bank-accounts',
        label: 'Rachunki bankowe',
        icon: ICONS.sidebarItems.bankAccounts,
        href: '/finance/bank-accounts',
        module: 'cash_position',
        permissions: ['manage_bank_accounts', 'view_company_finance', 'view_all'],
      },
      {
        id: 'loans',
        label: 'Kredyty',
        icon: ICONS.sidebarItems.cards,
        href: '/finance/loans',
        module: 'cash_position',
        permissions: ['manage_loans', 'view_company_finance', 'view_all'],
      },
      {
        id: 'company-costs',
        label: 'Koszty stałe',
        icon: ICONS.sidebarItems.fixedCosts,
        href: '/finance/company-costs',
        module: 'cash_position',
        permissions: ['manage_company_costs', 'view_all'],
      },
      {
        id: 'advances',
        label: 'Zaliczki',
        icon: ICONS.sidebarItems.advances,
        href: '/finance/advances',
        module: 'cash_position',
        permissions: ['manage_advances', 'view_all'],
      },
      {
        id: 'invoices',
        label: 'Faktury',
        icon: ICONS.sidebarItems.invoices,
        href: '/finance/invoices',
        permissions: ['manage_invoices', 'view_all'],
      },
      {
        id: 'contractors',
        label: 'Kontrahenci',
        icon: 'team',
        href: '/finance/contractors',
        permissions: ['manage_contractors', 'view_all'],
      },
    ],
  },
  {
    module: 'admin',
    label: 'Admin',
    icon: ICONS.sidebarGroups.admin,
    items: [
      {
        id: 'users',
        label: 'Użytkownicy',
        icon: ICONS.sidebarItems.users,
        href: '/admin/users',
        permissions: ['manage_users', 'view_all'],
      },
      {
        id: 'administration',
        label: 'Administracja',
        icon: ICONS.sidebarGroups.admin,
        href: '/admin/administration',
      },
      {
        id: 'imports',
        label: 'Importy',
        icon: ICONS.sidebarItems.imports,
        href: '/finance/imports',
        module: 'finance',
        permissions: ['manage_imports', 'view_all'],
      },
    ],
  },
]

export function Shell() {
  const navigate = useNavigate()
  const location = useLocation()
  const user = useUser()

  const groups = useMemo<EuNavGroup[]>(() => {
    return MODULE_NAVS.filter((m) => !m.module || user.modules.includes(m.module))
      .map((m) => ({
        label: m.label,
        icon: m.icon,
        items: m.items
          .filter((it) => !it.module || user.modules.includes(it.module))
          .filter(
            (it) => !it.permissions || it.permissions.some((p) => user.permissions.includes(p)),
          )
          .map((it) => ({ id: it.id, label: it.label, icon: it.icon, href: it.href })),
      }))
      .filter((g) => g.items.length > 0)
  }, [user.modules, user.permissions])

  const activeId = useMemo(() => {
    const flat = groups.flatMap((g) => g.items)
    const match = flat
      .filter((it): it is EuNavItem & { href: string } => Boolean(it.href))
      .sort((a, b) => b.href.length - a.href.length)
      .find((it) => location.pathname.startsWith(it.href))
    return match?.id
  }, [groups, location.pathname])

  function handleNavigate(item: EuNavItem) {
    if (item.href) navigate(item.href)
  }

  const sidebar = (
    <EuSidebar
      module="metrix"
      groups={groups}
      active={activeId}
      brandLogo={<img src="/logo-black.png" alt="Metrix" className={styles.brandLogoImg} />}
      brandLabel="Metrix"
      showTagline={false}
      userMenu={<UserMenu />}
      onNavigate={handleNavigate}
    />
  )

  return (
    <EuShell module="metrix" sidebar={sidebar}>
      <main className={styles.main}>
        <Suspense fallback={<ShellContentFallback />}>
          <Outlet />
        </Suspense>
      </main>
      <CompanyContextBridge />
    </EuShell>
  )
}

function ShellContentFallback() {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'var(--space-6)',
      }}
    >
      <Spinner />
    </div>
  )
}
